import DataStore, { Query, QueryResult } from 'src/core/stores/data-store';
import FormStore from 'src/core/stores/form-store';
import { CommandResult } from 'src/core/stores/types';
import queryString from 'query-string';

export enum NotificationType {
  OrganizationCreationFinished = 'OrganizationCreationFinished',
  OrganizationExpirationWarning = 'OrganizationExpirationWarning',
  OrganizationIsAvailable = 'OrganizationIsAvailable',
  LicenseNeedsApproval = 'LicenseNeedsApproval',
  LicenseWasRenewed = 'LicenseWasRenewed',
  ForgotPassword = 'ForgotPassword',
  OrganizationInvitation = 'OrganizationInvitation',
  LicenseQRShared = 'LicenseQRShared',
  ToolUsageReport = 'ToolUsageReport',
}

export enum EntityType {
  Project = 'Project',
  Ring = 'Ring',
  Drillhole = 'Drillhole',
  Survey = 'Survey',
}

export const getValidEntityTypes = () => {
  return Object.values(EntityType);
};

export const getNotificationTypeTranslationKey = (type: NotificationType) => {
  return `${type}NotificationTitle`;
};

export type NotificationDataSummary = {
  id: string;
  notificationId: string;
  type: NotificationType;
  actionUrl: string;
  createdAt: string;
  isRead: boolean;
  count: number;
  redirectEntityId: string;
  redirectEntityName: string;
  redirectEntityType: EntityType;
  relatedEntityName: string;
  relatedEntityType: EntityType;
  relatedEntityId: string;
  error?: string;
};

export interface NotificationPreferencesSummary {
  inAppEnabled: boolean;
  emailEnabled: boolean;
  readNotificationRetentionMonths: number;
}

export enum NotificationDeleteOptions {
  OneMonth = 1,
  ThreeMonths = 3,
  SixMonths = 6,
}

export const getNotificationDeleteLabelFromDays = (days: number) => {
  return Object.entries(NotificationDeleteOptions).find(([key, value]) => value === days && isNaN(parseInt(key)))?.[0] ?? '';
};

export function getValidNotificationTypes(): NotificationType[] {
  return Object.values(NotificationType);
}

export class NotificationDataStore extends DataStore<NotificationDataSummary> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/api/notifications`, []);
  }

  public async getUnreadNotificationCount() {
    return await this.handleCallAsync(async () => {
      const notificationTypesFilter = `(${getValidNotificationTypes()
        .map((v) => `type eq '${v}'`)
        .join(' or ')})`;

      const query = {
        filter: [{ isRead: false }, notificationTypesFilter],
        take: 1,
        skip: 0,
        orderBy: [{ field: 'createdAt', direction: 'Descending', useProfile: false }],
      };
      var response = await this.httpService.get<QueryResult<NotificationDataSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query as Query)}`);
      return response.data.count ?? 0;
    });
  }

  public async markAsRead(id: string) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.put(`${this.baseUrl}/${id}/read`, {});
      return response.data;
    });
  }

  public async getNotifications(query: Partial<Query>) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<QueryResult<NotificationDataSummary>>(`${this.baseUrl}?${DataStore.buildUrl(query as Query)}`);
      return response.data;
    });
  }

  public async readAll() {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.put(`${this.baseUrl}/read-all`, {});
      return response.data;
    });
  }

  public async deleteAllUnread() {
    return await this.handleCallAsync(async () => {
      var query = queryString.stringify({ isRead: false });
      var response = await this.httpService.delete<{ isRead: boolean }, CommandResult<{ isRead: boolean }>>(`${this.baseUrl}/bulk?${query}`, {
        isRead: false,
      });
      return response.data;
    });
  }

  public async deleteAllRead() {
    return await this.handleCallAsync(async () => {
      var query = queryString.stringify({ isRead: true });
      var response = await this.httpService.delete<{ isRead: boolean }, CommandResult<{ isRead: boolean }>>(`${this.baseUrl}/bulk?${query}`, {
        isRead: true,
      });
      return response.data;
    });
  }
}

export class NotificationPreferencesStore extends FormStore<NotificationPreferencesSummary, NotificationPreferencesSummary> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/api/notifications/preferences`);
  }

  public async load(userId: string) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<NotificationPreferencesSummary>(`${this.baseUrl}`);

      this._state.set((s) => {
        s.item = response.data as NotificationPreferencesSummary;
        return s;
      });
      return true;
    });
  }

  public async updatePreferences(userId: string, item: NotificationPreferencesSummary) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.put<NotificationPreferencesSummary, CommandResult<NotificationPreferencesSummary>>(`${this.baseUrl}`, item);
      if (result.data.isSuccess) {
        this._state.set((s) => {
          s.item = item as NotificationPreferencesSummary;
          return s;
        });
      }
      return result.data;
    });
  }
}
